import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import productsService from '../services/products';
import AzureAddressAutocomplete from '../components/AzureAddressAutocomplete';

interface FormState {
  name: string;
  ready_mix_supplier: string;
  address: string;
  latitude: string;
  longitude: string;
}

const EMPTY_FORM: FormState = { name: '', ready_mix_supplier: '', address: '', latitude: '', longitude: '' };

const BatchingPlantForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { plantId } = useParams<{ plantId: string }>();
  const { user } = useAuth();
  const isEdit = Boolean(plantId);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [nameError, setNameError] = useState<string>('');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!plantId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const plant = await productsService.getSupplierPlant(plantId);
        if (!cancelled) {
          setForm({
            name: plant.name || '',
            ready_mix_supplier: plant.ready_mix_supplier || '',
            address: plant.address || '',
            latitude: plant.latitude || '',
            longitude: plant.longitude || '',
          });
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.response?.data?.detail || 'Failed to load batching plant');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [plantId]);

  const setField = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (field === 'name' && e.target.value.trim()) {
      setNameError('');
    }
  };

  const validate = (): boolean => {
    if (!form.name.trim()) {
      setNameError(t('batchingPlant.nameRequired', 'Plant name is required'));
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setError('');
    try {
      if (isEdit && plantId) {
        await productsService.updateSupplierPlant(plantId, {
          name: form.name.trim(),
          ready_mix_supplier: form.ready_mix_supplier.trim() || null,
          address: form.address.trim() || null,
          latitude: form.latitude.trim() || null,
          longitude: form.longitude.trim() || null,
        });
        navigate(`/batching-plants/${plantId}`);
      } else {
        const created = await productsService.createSupplierPlant({
          name: form.name.trim(),
          company_id: user?.CompanyID || '',
          ready_mix_supplier: form.ready_mix_supplier.trim() || undefined,
          address: form.address.trim() || undefined,
          latitude: form.latitude.trim() || undefined,
          longitude: form.longitude.trim() || undefined,
        });
        navigate(`/batching-plants/${created.supplier_plant_id}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save batching plant');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Slim header — the app bar carries the page title */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/batching-plants')}
          sx={{ textTransform: 'none' }}
        >
          {t('common.back', 'Back')}
        </Button>
      </Box>

      <Paper elevation={2} sx={{ p: 3, maxWidth: 760 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            required
            size="small"
            label={t('batchingPlant.plantName', 'Batching Plant Name')}
            value={form.name}
            onChange={setField('name')}
            error={Boolean(nameError)}
            helperText={nameError}
            inputProps={{ maxLength: 200 }}
          />
          <TextField
            fullWidth
            size="small"
            label={t('batchingPlant.readyMixSupplier', 'Ready Mix Supplier')}
            value={form.ready_mix_supplier}
            onChange={setField('ready_mix_supplier')}
            inputProps={{ maxLength: 200 }}
          />
          <Box>
            <AzureAddressAutocomplete
              label={t('batchingPlant.address', 'Address')}
              value={form.address}
              placeholder="e.g. 4/3 Ascot Road, Ballina NSW 2478"
              onChange={(address, lat, lon) => {
                setForm((prev) => ({
                  ...prev,
                  address,
                  ...(lat && lon ? { latitude: lat, longitude: lon } : {}),
                }));
              }}
            />
            {form.latitude && form.longitude && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {t('batchingPlant.gpsLocation', 'GPS Location')}: {form.latitude}, {form.longitude}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 3,
              justifyContent: 'center',
              alignItems: 'center',
              mt: 1,
              pt: 2,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || !form.name.trim()}
              sx={{
                textTransform: 'none',
                px: 4,
                backgroundColor: '#9AD02E',
                '&:hover': { backgroundColor: '#88ba28' },
              }}
            >
              {isEdit
                ? t('batchingPlant.updatePlant', 'Update Batch Plant')
                : t('batchingPlant.createPlant', 'Create Batch Plant')}
            </Button>
            <Button
              onClick={() => navigate('/batching-plants')}
              disabled={saving}
              sx={{ textTransform: 'none' }}
            >
              {t('batchingPlant.cancelReturn', 'Cancel / Return')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default BatchingPlantForm;
